<template>
  <div class="row">
    <div class="col-lg-12 col-xxl-12">
      <div class="card card-custom card-stretch gutter-b">
        <div class="card-header border-0 py-5">
          <h3 class="card-title align-items-start flex-column">
            <span class="card-label font-weight-bolder text-dark">
              จัดการสแกนแลกแต้ม
            </span>
            <span class="text-muted mt-2 font-weight-bold font-size-sm">
              หน้ารายละเอียดและจัดการข้อมูลสแกนแลกแต้ม (ID: {{ qrId }})
            </span>
          </h3>
        </div>
        <div class="card-body pb-10 pt-5">
          <div v-if="isLoading" class="text-center py-10">
            <b-spinner></b-spinner>
          </div>
          <div v-else-if="loadError" class="text-center py-10">
            <p class="text-muted">
              ไม่พบข้อมูลสแกนแลกแต้ม หรือไม่มีสิทธิ์เข้าถึง
            </p>
            <b-button variant="secondary" @click.prevent="cancel()">
              {{ $t("PAGE.DEFAULT.FORM.BTN_CANCEL") }}
            </b-button>
          </div>
          <b-form v-else @submit.stop.prevent="onSubmit()">
            <!-- ชื่อ -->
            <b-row class="mb-5 align-items-center">
              <b-col sm="3">
                <label class="mb-0">
                  <h5 class="mb-0">
                    {{ $t("PAGE.DEFAULT.FORM.FIELDS.NAME") }}
                  </h5>
                </label>
              </b-col>
              <b-col sm="9">
                <b-form-input
                  id="qr-name-input"
                  name="qr-name-input"
                  type="text"
                  v-model="$v.form.name.$model"
                  :state="validateState('name')"
                  placeholder="ระบุชื่อสแกนแลกแต้ม"
                  aria-describedby="name-live-feedback"
                ></b-form-input>
                <b-form-invalid-feedback id="name-live-feedback">
                  <span v-if="!$v.form.name.required" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.REQUIRED") }}
                  </span>
                  <span v-else-if="!$v.form.name.maxLength" class="error">
                    ชื่อต้องไม่เกิน {{ MAX_NAME_LENGTH }} ตัวอักษร
                  </span>
                </b-form-invalid-feedback>
              </b-col>
            </b-row>

            <!-- แต้มที่ได้รับ -->
            <b-row class="mb-5 align-items-center">
              <b-col sm="3">
                <label class="mb-0">
                  <h5 class="mb-0">
                    {{ $t("PAGE.CAMPAIGN.FORM.CAMPAIGN_REWARD.POINT") }}
                  </h5>
                </label>
              </b-col>
              <b-col sm="9">
                <b-form-input
                  id="qr-point-input"
                  name="qr-point-input"
                  type="number"
                  min="1"
                  step="1"
                  @wheel="$event.target.blur()"
                  v-model.number="$v.form.point.$model"
                  :state="validateState('point')"
                  placeholder="ระบุจำนวนแต้ม"
                  aria-describedby="point-live-feedback"
                ></b-form-input>
                <b-form-invalid-feedback id="point-live-feedback">
                  <span v-if="!$v.form.point.required" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.REQUIRED") }}
                  </span>
                  <span v-else class="error">
                    จำนวนแต้มต้องเป็นจำนวนเต็ม 1 -
                    {{ MAX_POINT.toLocaleString("th-TH") }}
                  </span>
                </b-form-invalid-feedback>
                <b-form-text v-if="scanCount > 0" class="text-warning small">
                  มีลูกค้าสแกนไปแล้ว
                  {{ scanCount.toLocaleString("th-TH") }} ครั้ง
                  การแก้แต้มจะมีผลเฉพาะการสแกนครั้งต่อไป
                </b-form-text>
              </b-col>
            </b-row>

            <!-- วันเริ่มใช้งาน -->
            <b-row class="mb-5 align-items-center">
              <b-col sm="3">
                <label class="mb-0">
                  <h5 class="mb-0">วันเริ่มใช้งาน</h5>
                </label>
              </b-col>
              <b-col sm="9">
                <date-picker
                  id="qr-start-datepicker"
                  v-model="$v.form.start_at.$model"
                  :config="options"
                  autocomplete="off"
                  placeholder="ไม่ระบุ = ใช้ได้ตั้งแต่เริ่มแคมเปญ"
                ></date-picker>
              </b-col>
            </b-row>

            <!-- วันหมดอายุ -->
            <b-row class="mb-5 align-items-center">
              <b-col sm="3">
                <label class="mb-0">
                  <h5 class="mb-0">วันหมดอายุ</h5>
                </label>
              </b-col>
              <b-col sm="9">
                <date-picker
                  id="qr-expire-datepicker"
                  v-model="$v.form.end_at.$model"
                  :config="options"
                  autocomplete="off"
                  placeholder="ไม่ระบุ = ใช้ได้จนจบแคมเปญ"
                ></date-picker>
                <span
                  v-if="$v.form.end_at.$dirty && !$v.form.end_at.endAfterStart"
                  class="error text-danger d-block mt-1"
                >
                  วันหมดอายุต้องอยู่หลังวันเริ่มใช้งาน
                </span>
              </b-col>
            </b-row>

            <!-- สถานะ -->
            <b-row class="mb-5 align-items-center">
              <b-col sm="3">
                <label class="mb-0">
                  <h5 class="mb-0">สถานะ</h5>
                </label>
              </b-col>
              <b-col sm="9">
                <b-form-checkbox
                  id="qr-status-switch"
                  switch
                  size="lg"
                  v-model="form.is_active"
                  name="is_active"
                >
                  <span
                    class="font-weight-bold"
                    :class="form.is_active ? 'text-success' : 'text-danger'"
                  >
                    {{ form.is_active ? "เปิดการใช้งาน" : "ปิดการใช้งาน" }}
                  </span>
                </b-form-checkbox>
                <b-form-text class="text-muted small">
                  เปิดหรือปิดการสแกนรับแต้มจาก QR Code นี้
                </b-form-text>
              </b-col>
            </b-row>

            <!-- ดาวน์โหลด QR Code & พรีวิว -->
            <b-row class="mb-5">
              <b-col sm="3">
                <label class="pt-2">
                  <h5 class="mb-0">QR Code</h5>
                </label>
              </b-col>
              <b-col sm="9">
                <div class="d-flex flex-column align-items-start">
                  <div
                    class="qr-preview-wrapper mb-3 p-3 bg-white rounded border"
                  >
                    <img
                      v-if="qrPreviewUrl"
                      :src="qrPreviewUrl"
                      alt="QR Code"
                      class="qr-code-img"
                    />
                  </div>
                  <b-button
                    variant="primary"
                    size="sm"
                    style="width: 150px"
                    :disabled="!qrPayload"
                    @click="downloadQr()"
                  >
                    <i class="flaticon2-download icon-sm mr-1"></i>
                    ดาวน์โหลด QR Code
                  </b-button>
                </div>
              </b-col>
            </b-row>

            <!-- ปุ่มดำเนินการด้านล่าง -->
            <b-row class="mt-8 pt-5 border-top">
              <b-col sm="12">
                <b-button
                  class="float-right"
                  variant="secondary"
                  @click.prevent="cancel()"
                >
                  {{ $t("PAGE.DEFAULT.FORM.BTN_CANCEL") }}
                </b-button>
                <b-button
                  type="submit"
                  variant="primary"
                  class="float-right mr-3"
                  :disabled="isSubmitting"
                >
                  <b-spinner v-if="isSubmitting" small class="mr-1"></b-spinner>
                  {{ $t("PAGE.DEFAULT.FORM.BTN_SUBMIT") }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.qr-preview-wrapper {
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
}
.qr-code-img {
  width: 130px;
  height: 130px;
  object-fit: contain;
}
</style>

<script>
import { validationMixin } from "vuelidate";
import {
  required,
  minValue,
  maxValue,
  maxLength,
  integer
} from "vuelidate/lib/validators";
import datePicker from "vue-bootstrap-datetimepicker";
import Swal from "sweetalert2";
import ApiService from "@/core/services/api.service";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  STATUS_ACTIVE,
  STATUS_INACTIVE,
  MAX_POINT,
  MAX_NAME_LENGTH,
  PICKER_FORMAT,
  buildQrPayload,
  toQrDataUrl,
  downloadQrImage,
  toPickerValue,
  toApiDate,
  endAfterStart,
  manageRoute
} from "./qrRedeem.helper";

export default {
  name: "QrRedeemManage",
  mixins: [validationMixin],
  components: {
    datePicker
  },
  data() {
    return {
      MAX_POINT,
      MAX_NAME_LENGTH,
      isLoading: true,
      loadError: false,
      isSubmitting: false,
      qr: null,
      qrPreviewUrl: "",
      form: {
        name: "",
        point: null,
        start_at: "",
        end_at: "",
        is_active: true
      },
      options: {
        useCurrent: false,
        locale: "th",
        format: PICKER_FORMAT,
        icons: {
          time: "fas fa-clock",
          date: "far fa-calendar",
          up: "fas fa-arrow-up",
          down: "fas fa-arrow-down",
          previous: "fas fa-chevron-left",
          next: "fas fa-chevron-right",
          today: "fas fa-calendar-check",
          clear: "far fa-trash-alt",
          close: "far fa-times-circle"
        }
      }
    };
  },
  validations: {
    form: {
      name: { required, maxLength: maxLength(MAX_NAME_LENGTH) },
      point: {
        required,
        integer,
        minValue: minValue(1),
        maxValue: maxValue(MAX_POINT)
      },
      start_at: {},
      end_at: { endAfterStart }
    }
  },
  computed: {
    campaignId() {
      return this.$route.params.campaignId;
    },
    companyId() {
      return this.$route.params.companyId;
    },
    qrId() {
      return this.$route.params.id;
    },
    backRoute() {
      return manageRoute(this.$route, this.campaignId);
    },
    qrPayload() {
      return this.qr ? buildQrPayload(this.qr.code) : "";
    },
    scanCount() {
      return Number(this.qr && this.qr.scan_count) || 0;
    }
  },
  watch: {
    async qrPayload(payload) {
      try {
        this.qrPreviewUrl = payload ? await toQrDataUrl(payload, 260) : "";
      } catch (e) {
        this.qrPreviewUrl = "";
      }
    }
  },
  mounted() {
    this.initBreadcrumb();
    this.loadData();
  },
  methods: {
    initBreadcrumb() {
      const current = { title: `จัดการสแกนแลกแต้ม (#${this.qrId || ""})` };
      const campaignForm = {
        title: `${this.$t("PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TITLE")}`,
        route: this.backRoute
      };
      if (this.companyId) {
        this.$store.dispatch(SET_BREADCRUMB, [
          {
            title: `${this.$t("MENU.COMPANY_MANAGEMENT.TITLE")}`,
            route: "/company-management"
          },
          {
            title: `${this.$t("MENU.CAMPAIGN_MANAGEMENT.CAMPAIGN")}`,
            route: `/company-management/${this.companyId}/campaign`
          },
          campaignForm,
          current
        ]);
      } else {
        this.$store.dispatch(SET_BREADCRUMB, [
          {
            title: `${this.$t("MENU.CAMPAIGN_MANAGEMENT.CAMPAIGN")}`,
            route: "/campaign"
          },
          campaignForm,
          current
        ]);
      }
    },
    applyQr(qr) {
      this.qr = qr;
      this.form = {
        name: qr.name || "",
        point: Number(qr.point),
        start_at: toPickerValue(qr.start_at),
        end_at: toPickerValue(qr.end_at),
        is_active: Number(qr.status_id) === STATUS_ACTIVE
      };
      this.$v.form.$reset();
    },
    async loadData() {
      this.isLoading = true;
      this.loadError = false;
      try {
        ApiService.setHeader();
        const [detail, scans] = await Promise.all([
          ApiService.getParams(
            `campaign/${this.campaignId}/qr_point/${this.qrId}`
          ),
          // จำนวนสแกนใช้แค่แสดงคำเตือน โหลดไม่ได้ก็ยังแก้ไข QR ได้
          ApiService.getParams(
            `campaign/${this.campaignId}/qr_point/${this.qrId}/scans`,
            { params: { page: 1, perPage: 1 } }
          ).catch(() => ({ data: {} }))
        ]);
        if (detail.data.status !== "success" || !detail.data.data) {
          throw new Error(detail.data.error);
        }
        const scanTotal =
          scans.data.status === "success" && scans.data.data
            ? Number(scans.data.data.total) || 0
            : 0;
        this.applyQr({ ...detail.data.data, scan_count: scanTotal });
      } catch (e) {
        this.loadError = true;
      } finally {
        this.isLoading = false;
      }
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    async onSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError || this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;
      try {
        ApiService.setHeader();
        const base = `campaign/${this.campaignId}/qr_point/${this.qrId}`;
        const { data } = await ApiService.put(base, {
          name: this.form.name.trim(),
          point: Number(this.form.point),
          start_at: toApiDate(this.form.start_at),
          end_at: toApiDate(this.form.end_at)
        });
        if (data.status !== "success") {
          throw new Error(data.error);
        }
        let saved = data.data;

        const statusId = this.form.is_active ? STATUS_ACTIVE : STATUS_INACTIVE;
        if (Number(saved.status_id) !== statusId) {
          const res = await ApiService.put(`${base}/status`, {
            status_id: statusId
          });
          if (res.data.status !== "success") {
            throw new Error(res.data.error);
          }
          saved = res.data.data;
        }
        this.applyQr({
          ...saved,
          scan_count: this.scanCount
        });

        Swal.fire({
          icon: "success",
          title: "บันทึกข้อมูลเรียบร้อย",
          timer: 1500,
          showConfirmButton: false
        });
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: "บันทึกข้อมูลไม่สำเร็จ",
          text: "กรุณาตรวจสอบข้อมูลแล้วลองใหม่อีกครั้ง"
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    cancel() {
      this.$router.push(this.backRoute);
    },
    async downloadQr() {
      try {
        await downloadQrImage(this.qrPayload, `qr-redeem-${this.qrId}`);
      } catch (e) {
        Swal.fire({ icon: "error", title: "สร้าง QR Code ไม่สำเร็จ" });
      }
    }
  }
};
</script>
